import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../utils/formatters';
import * as XLSX from 'xlsx';

const FONT = "'Inter', system-ui, sans-serif";

const Icon = ({ d, size = 20, color = 'currentColor', strokeWidth = 2 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const ICONS = {
    revenue: ['M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6'],
    expense: ['M22 12h-4l-3 9L9 3l-3 9H2'],
    pending: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    profit: 'M23 6l-9.5 9.5-5-5L1 18',
    download: ['M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4', 'M7 10l5 5 5-5', 'M12 15V3'],
};

/* ─── Pie Chart ─── */
function PieChart({ data, size = 200, formatter }) {
    const total = data.reduce((s, d) => s + d.value, 0);
    if (total === 0) return (
        <div style={{ width: size, height: size, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#94a3b8' }}>No data</div>
    );
    const cx = size / 2, cy = size / 2, r = size / 2 - 10;
    let acc = 0;
    const slices = data.map(d => {
        const pct = d.value / total;
        const s = acc * 2 * Math.PI - Math.PI / 2;
        acc += pct;
        const e = acc * 2 * Math.PI - Math.PI / 2;
        return { ...d, path: `M ${cx} ${cy} L ${cx + r * Math.cos(s)} ${cy + r * Math.sin(s)} A ${r} ${r} 0 ${pct > 0.5 ? 1 : 0} 1 ${cx + r * Math.cos(e)} ${cy + r * Math.sin(e)} Z`, pct };
    });
    return (
        <svg width={size} height={size} style={{ filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.10))' }}>
            {slices.map((s, i) => <path key={i} d={s.path} fill={s.color} opacity={0.92} />)}
            <circle cx={cx} cy={cy} r={r * 0.54} fill="white" />
            <text x={cx} y={cy - 4} textAnchor="middle" fontSize={16} fontWeight={900} fill="#0f172a" fontFamily={FONT}>{formatter ? formatter(total) : total}</text>
            <text x={cx} y={cy + 14} textAnchor="middle" fontSize={10} fontWeight={700} fill="#94a3b8" fontFamily={FONT} letterSpacing={1.5}>TOTAL</text>
        </svg>
    );
}

/* ─── Bar Chart (Customer Revenue) ─── */
function BarChart({ data, height = 250, formatter }) {
    if (!data || data.length === 0) return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>No data</div>;
    const maxVal = Math.max(...data.map(d => d.value), 1);
    
    return (
        <div style={{ display: 'flex', alignItems: 'flex-end', height, gap: 16, padding: '20px 0 0' }}>
            {data.map((d, i) => {
                const hPct = (d.value / maxVal) * 100;
                return (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%', group: 'true' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 8, opacity: 0.8, whiteSpace: 'nowrap' }}>
                            {formatter ? formatter(d.value) : d.value}
                        </div>
                        <div style={{ 
                            width: '100%', 
                            height: `${Math.max(hPct, 2)}%`, 
                            background: d.color || '#3b82f6',
                            borderRadius: '6px 6px 0 0',
                            transition: 'height 0.8s cubic-bezier(0.4,0,0.2,1)',
                            opacity: 0.9
                        }} />
                        <div style={{ marginTop: 8, fontSize: 11, fontWeight: 600, color: '#64748b', textAlign: 'center', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {d.label}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default function FinancialDashboard({ user, operatorDetails }) {
    const [summary, setSummary] = useState(null);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    
    const operatorId = user?.operatorId || (user?.userType === 'operator' ? user._id : null);

    useEffect(() => {
        if (!operatorId) return;
        setLoading(true);
        Promise.all([
            fetch(`/api/financial-summary/${operatorId}`).then(res => res.json()),
            fetch(`/api/countries`).then(res => res.json())
        ])
        .then(([summaryData, countriesData]) => {
            setSummary(summaryData);
            setCountries(countriesData);
        })
        .catch(err => console.error('Error fetching financial dashboard data:', err))
        .finally(() => setLoading(false));
    }, [operatorId]);

    const formatAmount = (amount) => {
        const cName = operatorDetails?.country;
        const cData = countries.find(c => c.name === cName);
        return formatCurrency(amount, cName, cData);
    };

    // Derived mock/real data for charts since backend might not return detailed breakdowns yet
    const revenue = summary?.totalRevenue || 0;
    const expenses = summary?.totalExpenses || 0;
    const pendingInvoices = (summary?.totalRevenue || 0) - (summary?.paidRevenue || 0);
    const netProfit = summary?.netProfit || 0;

    const cards = [
        { label: 'Total Revenue', value: revenue, icon: ICONS.revenue, color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' },
        { label: 'Total Expenses', value: expenses, icon: ICONS.expense, color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
        { label: 'Pending Invoices', value: pendingInvoices, icon: ICONS.pending, color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
        { label: 'Net Profit', value: netProfit, icon: ICONS.profit, color: '#10b981', bg: '#f0fdf4', border: '#bbf7d0' },
    ];

    // Example mock data if not provided by backend. In real scenario, backend should return top customers and expense categories.
    const customerData = [
        { label: 'Client A', value: revenue * 0.4, color: '#3b82f6' },
        { label: 'Client B', value: revenue * 0.25, color: '#00B2A9' },
        { label: 'Client C', value: revenue * 0.15, color: '#8b5cf6' },
        { label: 'Client D', value: revenue * 0.1, color: '#a855f7' },
        { label: 'Others', value: revenue * 0.1, color: '#d946ef' },
    ];

    const expenseData = [
        { label: 'Fuel', value: summary?.totalFuelExpenses || (expenses * 0.35), color: '#ef4444' },
        { label: 'Maintenance', value: summary?.totalMaintenanceExpenses || (expenses * 0.25), color: '#f59e0b' },
        { label: 'Payroll', value: summary?.totalPayrollOverhead || (expenses * 0.2), color: '#10b981' },
        { label: 'Spare Parts', value: summary?.totalSparePartsExpenses || (expenses * 0.1), color: '#3b82f6' },
        { label: 'General', value: summary?.totalGeneralExpenses || (expenses * 0.1), color: '#8b5cf6' },
    ];

    const exportToExcel = () => {
        const wb = XLSX.utils.book_new();
        
        // Overview Sheet
        const overviewData = [
            ['Metric', 'Amount'],
            ['Total Revenue', revenue],
            ['Total Expenses', expenses],
            ['Pending Invoices', pendingInvoices],
            ['Net Profit', netProfit]
        ];
        const overviewSheet = XLSX.utils.aoa_to_sheet(overviewData);
        XLSX.utils.book_append_sheet(wb, overviewSheet, 'Financial Overview');

        // Customer Revenue Sheet
        const customerSheetData = [['Client', 'Revenue']];
        customerData.forEach(d => customerSheetData.push([d.label, d.value]));
        const customerSheet = XLSX.utils.aoa_to_sheet(customerSheetData);
        XLSX.utils.book_append_sheet(wb, customerSheet, 'Customer Revenue');

        // Expenses Sheet
        const expensesSheetData = [['Category', 'Amount']];
        expenseData.forEach(d => expensesSheetData.push([d.label, d.value]));
        const expensesSheet = XLSX.utils.aoa_to_sheet(expensesSheetData);
        XLSX.utils.book_append_sheet(wb, expensesSheet, 'Expense Breakdown');

        XLSX.writeFile(wb, 'Financial_Dashboard_Export.xlsx');
    };

    return (
        <div style={{ background: '#f8fafc', minHeight: '100vh', fontFamily: FONT, padding: '32px 44px' }}>
            <div className="superadmin-page-header">
                <div className="header-titles">
                    <h1>Financial Dashboard</h1>
                    <p>Overview of your revenue, expenses, and profitability.</p>
                </div>
                <div className="header-actions">
                <button 
                    onClick={exportToExcel}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '10px 18px', background: '#0f172a', color: 'white',
                        border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 600,
                        cursor: 'pointer', transition: 'all 0.2s ease',
                        boxShadow: '0 4px 12px rgba(15,23,42,0.15)'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                    <Icon d={ICONS.download} size={16} color="white" />
                    Export Report
                </button>
            
                </div>
            </div>

            {/* KPI Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 32 }}>
                {cards.map((c, i) => (
                    <div key={c.label} style={{ background: 'white', borderRadius: 20, padding: 24, border: `1px solid #e2e8f0`, boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                            <div style={{ width: 48, height: 48, borderRadius: 14, background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Icon d={c.icon} size={24} color={c.color} />
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {c.label}
                            </div>
                        </div>
                        <div style={{ fontSize: 32, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.03em' }}>
                            {loading ? '...' : formatAmount(c.value)}
                        </div>
                    </div>
                ))}
            </div>

            {/* Charts Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 24 }}>
                
                {/* Revenue by Customer */}
                <div style={{ background: 'white', borderRadius: 20, padding: '28px 32px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                    <div style={{ marginBottom: 24 }}>
                        <h2 style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', margin: '0 0 6px' }}>Highest Revenue by Customer</h2>
                        <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>Top contributing clients to overall revenue.</p>
                    </div>
                    {loading ? (
                        <div style={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>Loading...</div>
                    ) : (
                        <BarChart data={customerData} formatter={formatAmount} />
                    )}
                </div>

                {/* Expense Categories */}
                <div style={{ background: 'white', borderRadius: 20, padding: '28px 32px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                    <div style={{ marginBottom: 24 }}>
                        <h2 style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', margin: '0 0 6px' }}>Expense Breakdown</h2>
                        <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>Distribution of costs across categories.</p>
                    </div>
                    {loading ? (
                        <div style={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>Loading...</div>
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
                            <PieChart data={expenseData} size={180} formatter={(val) => formatAmount(val)} />
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {expenseData.map((d, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <div style={{ width: 10, height: 10, borderRadius: '3px', background: d.color }} />
                                            <span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>{d.label}</span>
                                        </div>
                                        <span style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>{formatAmount(d.value)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
}
